import { BiTargetLock } from 'react-icons/bi'
import { FiInfo, FiLayers, FiTarget } from 'react-icons/fi'
import { RiAlarmLine, RiAlertLine, RiTextSnippet } from 'react-icons/ri'
import { TbTargetArrow } from 'react-icons/tb'
import { MapRef } from 'react-map-gl/mapbox'

import { useMapContext } from './MapContext'
import { MapGeofenceSelection } from './MapGeofenceSelection'
import { getCenterFromBounds, zoomToCenter } from './utils'

interface MapLayerToggles {
  mapRef: React.RefObject<MapRef | null>
}

export const MapLayerToggles = ({ mapRef }: MapLayerToggles) => {
  const context = useMapContext()

  // Always recenters on the majority of assets. If an asset is selected, deselect it first so
  // this button behaves consistently (never recenters on the selected asset).
  const handleRecenterMajority = () => {
    if (context.currentAssetId) {
      context.setCurrentAssetId(undefined)
    }

    const assets = context.filteredAssets
    if (assets.length === 0) return

    const map = mapRef.current?.getMap()
    if (!map) return

    const points = assets.map((a) => ({
      latitude: a.location.latitude,
      longitude: a.location.longitude,
    }))

    const { bounds, zoom } = zoomToCenter(points, 0, 15)
    const center = getCenterFromBounds(bounds)

    map.flyTo({ center: [center.longitude, center.latitude], zoom, duration: 1000 })
  }

  // Recenters on the currently selected asset (only used by the asset-specific control).
  const handleRecenterAsset = () => {
    const map = mapRef.current?.getMap()
    if (!map || !context.currentAssetData) return
    const { longitude, latitude } = context.currentAssetData.location
    map.flyTo({ center: [longitude, latitude], zoom: 14, duration: 1000 })
  }

  return (
    <>
      {/* <DesktopContent /> */}
      <div className="absolute top-4 left-4 z-10 md:flex flex-col gap-1 hidden">
        {/* General map toggles - always visible */}
        <button
          onClick={handleRecenterMajority}
          className="tooltip tooltip-right tooltip-primary btn btn-sm btn-square bg-base-100 shadow-lg border border-base-300 hover:bg-base-200"
          data-tip="Recenter map on majority of assets"
        >
          <FiTarget className="w-4 h-4" />
        </button>
        <button
          data-tip="Toggle asset clustering on/off"
          onClick={() => {
            context.setMapState({ ...context.mapState, clustersEnabled: !context.mapState.clustersEnabled })
          }}
          className={`tooltip tooltip-right tooltip-primary btn btn-sm btn-square shadow-lg border border-base-300 ${
            context.mapState.clustersEnabled ? 'btn-primary' : 'bg-base-100 hover:bg-base-200'
          }`}
        >
          <FiLayers className="w-4 h-4" />
        </button>
        <MapGeofenceSelection />
        <button
          data-tip="Toggle asset labels on/off"
          onClick={() => {
            context.setMapState({ ...context.mapState, assetLabels: !context.mapState.assetLabels })
          }}
          className={`tooltip tooltip-right tooltip-primary btn btn-sm btn-square shadow-lg border border-base-300 ${
            context.mapState.assetLabels ? 'btn-primary' : 'bg-base-100 hover:bg-base-200'
          }`}
        >
          <RiTextSnippet className="w-4 h-4" />
        </button>
        <button
          data-tip="Toggle between visualizing highest severity of alarm vs alert"
          onClick={() => {
            context.setMapState({
              ...context.mapState,
              severitySource: context.mapState.severitySource === 'alarms' ? 'alerts' : 'alarms',
            })
          }}
          className="tooltip tooltip-right tooltip-primary btn btn-sm btn-square bg-base-100 shadow-lg border border-base-300 hover:bg-base-200"
        >
          {context.mapState.severitySource === 'alarms' ? (
            <RiAlarmLine className="w-4 h-4" />
          ) : (
            <RiAlertLine className="w-4 h-4" />
          )}
        </button>

        {/* Asset-specific toggles - only visible when an asset is selected */}
        {context.currentAssetId && (
          <div className="flex flex-col gap-1 mt-2 pt-2 border-t border-base-300">
            <button
              onClick={handleRecenterAsset}
              className="tooltip tooltip-right tooltip-primary btn btn-sm btn-square bg-base-100 shadow-lg border border-base-300 hover:bg-base-200"
              data-tip="Recenter map on selected asset"
            >
              <TbTargetArrow className="w-4 h-4 fill-amber-400" />
            </button>
            <button
              data-tip="Hide all but selected"
              onClick={() => {
                context.setMapState({ ...context.mapState, focus: !context.mapState.focus })
              }}
              className={`tooltip tooltip-right tooltip-primary btn btn-sm btn-square shadow-lg border border-base-300 ${
                context.mapState.focus ? 'btn-primary' : 'bg-base-100 hover:bg-base-200'
              }`}
            >
              <BiTargetLock className="w-4 h-4" />
            </button>
            <button
              id="quick-view-toggle"
              data-tip="Toggle asset quick view"
              onClick={() => {
                context.setMapState({ ...context.mapState, showQuickView: !context.mapState.showQuickView })
              }}
              className={`tooltip tooltip-right tooltip-primary btn btn-sm btn-square shadow-lg border border-base-300 ${
                context.mapState.showQuickView ? 'btn-primary' : 'bg-base-100 hover:bg-base-200'
              }`}
            >
              <FiInfo className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
      {/* <MobileContent /> */}
      <div className="absolute top-18 left-4 z-10 md:hidden">
        <div className="flex gap-2 items-center overflow-x-auto no-scrollbar w-fit max-w-[calc(100vw-2rem)]">
          <button
            onClick={handleRecenterMajority}
            className="shrink-0 tooltip tooltip-right tooltip-primary btn text-xs rounded-full bg-base-100 shadow-sm border border-base-300 hover:bg-base-200"
            data-tip="Recenter map on majority of assets"
          >
            <FiTarget className="w-4 h-4" />
            Recenter
          </button>
          <button
            data-tip="Toggle asset clustering on/off"
            onClick={() => {
              context.setMapState({ ...context.mapState, clustersEnabled: !context.mapState.clustersEnabled })
            }}
            className={`shrink-0 tooltip tooltip-right tooltip-primary btn  text-xs rounded-full shadow-sm border border-base-300 ${
              context.mapState.clustersEnabled ? 'btn-primary' : 'bg-base-100 hover:bg-base-200'
            }`}
          >
            <FiLayers className="w-4 h-4" />
            Toggle Cluster
          </button>
          {/* Need to do something about this */}
          {/* <MapGeofenceSelection /> */}
          <button
            data-tip="Toggle asset labels on/off"
            onClick={() => {
              context.setMapState({ ...context.mapState, assetLabels: !context.mapState.assetLabels })
            }}
            className={`shrink-0 tooltip tooltip-right tooltip-primary btn rounded-full shadow-sm border border-base-300 ${
              context.mapState.assetLabels ? 'btn-primary' : 'bg-base-100 hover:bg-base-200'
            }`}
          >
            <RiTextSnippet className="w-4 h-4" />
            Labels
          </button>
          <button
            data-tip="Toggle between visualizing highest severity of alarm vs alert"
            onClick={() => {
              context.setMapState({
                ...context.mapState,
                severitySource: context.mapState.severitySource === 'alarms' ? 'alerts' : 'alarms',
              })
            }}
            className="shrink-0 tooltip tooltip-right tooltip-primary btn rounded-full bg-base-100 shadow-sm border border-base-300 hover:bg-base-200"
          >
            {context.mapState.severitySource === 'alarms' ? (
              <span className="flex items-center justify-center gap-2">
                <RiAlarmLine className="w-4 h-4" />
                Alarm
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <RiAlertLine className="w-4 h-4" />
                Alert
              </span>
            )}
          </button>

          {/* Asset-specific toggles - only visible when an asset is selected */}
          {context.currentAssetId && (
            <>
              <button
                onClick={handleRecenterAsset}
                className="shrink-0 tooltip tooltip-right tooltip-primary btn rounded-full bg-base-100 shadow-sm border border-base-300 hover:bg-base-200"
                data-tip="Recenter map on selected asset"
              >
                <TbTargetArrow className="w-4 h-4 fill-amber-400" />
                Recenter Asset
              </button>
              <button
                data-tip="Hide all but selected"
                onClick={() => {
                  context.setMapState({ ...context.mapState, focus: !context.mapState.focus })
                }}
                className={`shrink-0 tooltip tooltip-right tooltip-primary btn rounded-full shadow-sm border border-base-300 ${
                  context.mapState.focus ? 'btn-primary' : 'bg-base-100 hover:bg-base-200'
                }`}
              >
                <BiTargetLock className="w-4 h-4" />
                Highlight Selected
              </button>
              <button
                id="quick-view-toggle"
                data-tip="Toggle asset quick view"
                onClick={() => {
                  context.setMapState({ ...context.mapState, showQuickView: !context.mapState.showQuickView })
                }}
                className={`shrink-0 tooltip tooltip-right tooltip-primary btn rounded-full shadow-sm border border-base-300 ${
                  context.mapState.showQuickView ? 'btn-primary' : 'bg-base-100 hover:bg-base-200'
                }`}
              >
                <FiInfo className="w-4 h-4" />
                Asset Quick View
              </button>
            </>
          )}
        </div>
      </div>
    </>
  )
}
